/** First week (8): real work on real data. Board learn-r2, missions 07 to 14. */
import type { Mission } from '../missions'
import { FLAG_US, GOOD_ASK, savedOnly, selfTick } from './shared'

export const firstWeek: Mission[] = [
  {
    id: 'add-a-screen',
    order: 7,
    title: 'Add a screen',
    track: 'first-week',
    say: "Add a screen that lists every retailer who hasn't ordered in six weeks, oldest first. I'll use it on Mondays to decide who to call.",
    happens: [
      'Your agent runs bun run new:screen. That adds the screen to the screen list, the page list and the server in one go.',
      'It builds the screen from the shared parts in src/ui, and writes a test for its numbers.',
      "The new screen shows in your sidebar as a draft. You and your team see it. People you share with don't.",
      'Your screen list grows by one, and this mission ticks.',
    ],
    check: { kind: 'registry' },
    watching: 'How many screens your app has, against the count when you opened this mission.',
    ifNot: [
      { symptom: 'No new screen in the sidebar.', say: 'Did bun run check pass? Show me the new screen in my preview.' },
      { symptom: 'The numbers look wrong.', say: 'Explain how you count "hasn\'t ordered in six weeks", and show me three retailers it found.' },
      { symptom: 'It says Example.', say: 'Is this screen on my real Shopify orders? If not, what do you need from me?' },
      FLAG_US,
    ],
    how: `This browser remembers how many screens your app had when you opened the mission, then compares. It's the same list cpg.computer already sees for sharing. ${savedOnly('Add a screen')}`,
    skill: 'add-screen',
    recipe: 'ask-for-a-screen',
    lesson: `${GOOD_ASK}\n\nThe sentence below has all four: the job (who to call), the data (orders), your rule (six weeks, oldest first) and who looks (you, on Mondays).`,
  },
  {
    id: 'connect-shopify',
    order: 8,
    title: 'Connect Shopify',
    track: 'first-week',
    act: 'do',
    say: 'Connect Shopify in your cpg.computer dashboard, under Connections. Then ask your agent: "Is my Today screen on real numbers now?"',
    happens: [
      'In app.cpg.computer, open Connections and connect Shopify. It can read your store, never change it.',
      'cpg.computer puts the keys on Railway for your live app, and in .env.local on your computer. Never in your code.',
      'Your app restarts with the keys and reads your real orders.',
      'The Example tag leaves your screens.',
    ],
    check: { kind: 'app-data', path: '/api/today' },
    watching: "Your Today screen's data, waiting for it to stop being example data.",
    ifNot: [
      { symptom: 'Connect Shopify shows an error.', say: 'Shopify refused my connection with this message: <paste it>. What should I do?' },
      { symptom: 'The Example tag is still there.', say: "Is my app reading Shopify yet? Check the keys arrived, and restart the preview if they did." },
      FLAG_US,
    ],
    how: `The page asks your own app whether its numbers are example data. Nothing leaves your app to check it. ${savedOnly('Connect Shopify')}`,
    skill: 'shopify',
    recipe: 'connect-shopify',
    needs: 'Connect Shopify works now. The check that sees your real numbers arrive comes in a Learn update. Until then, watch for the Example tag to go.',
  },
  {
    id: 'bring-a-file',
    order: 9,
    title: 'Bring a file',
    track: 'first-week',
    say: "Here's our retailer list. Use it on the Retailers screen.",
    happens: [
      'Attach your CSV in Paseo first, then say the sentence with it.',
      'Your agent saves it in data/, the folder for your own files, and reads it on the server.',
      'It checks the columns and tells you what it found: how many rows, which columns, anything odd.',
      "The screen shows the file's data. The file stays on the server; it never goes to the browser as it is.",
    ],
    check: { kind: 'self' },
    watching: 'Nothing on this page. Tick it once the screen shows your file.',
    ifNot: [
      { symptom: "The agent can't see the file.", say: "I attached the file but you can't see it. Where should I put it?" },
      { symptom: 'Some rows are missing.', say: 'How many rows did you read, and which ones did you skip, and why?' },
      FLAG_US,
    ],
    how: selfTick('Bring a file', "This page doesn't read your files."),
    skill: 'add-screen',
    recipe: 'bring-a-file',
  },
  {
    id: 'add-a-key',
    order: 10,
    title: 'Add a key for another service',
    track: 'first-week',
    say: 'I want to make ad images with Higgsfield. What key do you need, and where do I put it?',
    happens: [
      'Your agent names the key, for example HIGGSFIELD_API_KEY, and tells you where to get it.',
      'You add it in your dashboard under Connections, then Keys. Never in the chat, never in a file.',
      'cpg.computer sends it to your live app and your computer. Your app reads it on the server only.',
      'Until the key arrives, your agent builds with example data.',
    ],
    check: { kind: 'signal', signal: 'hasKey' },
    watching: 'A yes or no from cpg.computer: is there a key with the name your agent gave?',
    ifNot: [
      { symptom: 'The agent asks you to paste the key in the chat.', say: "Don't take keys in the chat. Tell me the name, and I'll add it under Keys." },
      { symptom: 'The app still uses example data.', say: 'I added the key. Is it on my computer and my live app yet?' },
      FLAG_US,
    ],
    how: `cpg.computer already knows which key names you've added (never the values). The page asks for a yes or no. ${savedOnly('Add a key for another service')}`,
    skill: 'connect-service',
    recipe: 'add-a-key',
    needs: 'Keys work now. This page will soon ask cpg.computer whether the key is there (a yes or no, never the key).',
  },
  {
    id: 'share-a-screen',
    order: 11,
    title: 'Share a screen',
    track: 'first-week',
    say: 'Make an investor view with totals only and no customer names, ready to share.',
    happens: [
      'Your agent adds the screen as a draft, with totals only.',
      'You check it in your preview, then say "Ship it". Your agent asks if the draft is ready to share.',
      'You share it from your dashboard with an email address.',
      'They sign in and see that one screen and nothing else.',
    ],
    check: { kind: 'signal', signal: 'hasGrant' },
    watching: 'A yes or no from cpg.computer: have you shared a screen?',
    ifNot: [
      { symptom: 'A customer name shows.', say: 'Take every customer name off the investor view. Totals only.' },
      { symptom: "The screen isn't in the sharing list.", say: 'Is the investor view still a draft? Make it ready to share and ship it.' },
      FLAG_US,
    ],
    how: `cpg.computer knows who you've shared with. The page asks for a yes or no. ${savedOnly('Share a screen')}`,
    skill: 'add-screen',
    recipe: 'share-a-screen',
    needs: 'Waits on sharing a screen from your dashboard.',
  },
  {
    id: 'pair-a-second-device',
    order: 12,
    title: 'Pair a second device',
    track: 'first-week',
    act: 'do',
    say: 'Press "Pair another device" on your cpg.computer dashboard, and scan the code with your laptop or iPad.',
    happens: [
      'Your dashboard shows a code for Paseo.',
      'Scan it with Paseo on the second device.',
      'Your project is there, with the same agent and the same chats.',
      'Send one message from the new device to check it works.',
    ],
    check: { kind: 'self' },
    watching: 'Nothing on this page. Tick it once the second device shows your project.',
    ifNot: [
      { symptom: 'The code expired.', say: 'Make a new code: press "Pair another device" again.' },
      { symptom: "The project isn't on the new device.", say: "I paired a second device but my project isn't there. What should I check?" },
      FLAG_US,
    ],
    how: selfTick('Pair a second device', "Paseo doesn't tell this page about pairings."),
    recipe: 'pair-a-device',
  },
  {
    id: 'read-your-computers-health',
    order: 13,
    title: "Read your computer's health",
    track: 'first-week',
    say: 'How is my computer doing?',
    happens: [
      'Your agent runs cpg status on your cpg.computer computer.',
      'It tells you in plain words: is it up, is there room on the disk, is the preview running, are updates waiting.',
      'It says whether anything needs you, and what to do.',
      'Nothing changes on your computer.',
    ],
    check: { kind: 'self' },
    watching: 'Nothing on this page. Tick it once your agent has answered.',
    ifNot: [
      { symptom: 'The answer is full of numbers.', say: 'Say that in two plain sentences. Is anything wrong?' },
      { symptom: 'Something needs fixing.', say: 'Can you fix that safely now? Tell me what you will do first.' },
      FLAG_US,
    ],
    how: selfTick("Read your computer's health", "This page can't see your chat."),
    skill: 'cpg',
    recipe: 'your-computers-health',
  },
  {
    id: 'when-something-breaks',
    order: 14,
    title: 'When something breaks',
    track: 'first-week',
    say: "Pretend the ship failed. Write me the note you'd send cpg.computer.",
    happens: [
      'Your agent writes the note: what you asked, what happened, what it tried, and the error in plain words.',
      'No keys and no customer data go in the note.',
      'You send it with "Flag us" in your dashboard, marked as practice.',
      'We answer, so you know the path works before you need it.',
    ],
    check: { kind: 'signal', signal: 'practiceFlag' },
    watching: 'A yes or no from cpg.computer: did your practice note reach us?',
    ifNot: [
      { symptom: 'The note has a key or a customer name in it.', say: 'Take every key and customer name out of the note.' },
      FLAG_US,
    ],
    how: `cpg.computer knows a practice note arrived. The page asks for a yes or no. ${savedOnly('When something breaks')}`,
    skill: 'flag-us',
    recipe: 'when-to-flag-us',
    needs: 'Waits on the "Flag us" button in your dashboard.',
  },
]
